<template>
    <div>
        <div
            class="rounded-xl bg-gray-50 p-4 text-sm text-gray-700"
        >
            <div class="flex flex-col gap-1">
                <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                    Search
                </label>
                <input
                    v-model.trim="searchTerm"
                    type="search"
                    placeholder="Search company or contact…"
                    class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                />
            </div>

            <div
                v-if="showFilters"
                class="grid gap-3 md:grid-cols-3 mt-4"
            >
                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        Subscription
                    </label>
                    <select
                        v-model="subscriptionFilter"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                    >
                        <option value="all">All subscriptions</option>
                        <option value="pro">Pro</option>
                        <option value="essentials">Essentials</option>
                        <option value="paid">Paid (Pro + Essentials)</option>
                        <option value="none">No paid subscription</option>
                    </select>
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        Status
                    </label>
                    <select
                        v-model="statusFilter"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                    >
                        <option value="all">All statuses</option>
                        <option value="active">Active only</option>
                        <option value="inactive">Not active</option>
                    </select>
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        Activity benchmark
                    </label>
                    <select
                        v-model="activityFilter"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                    >
                        <option value="any">All activity levels</option>
                        <option value="above">Above peers</option>
                        <option value="on_par">In line with peers</option>
                        <option value="below">Below peers</option>
                        <option value="unavailable">Benchmark unavailable</option>
                    </select>
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        Event attendance
                    </label>
                    <select
                        v-model="eventAttendanceFilter"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                    >
                        <option value="all">All contacts</option>
                        <option value="attended">Has attended</option>
                        <option value="not_attended">Never attended</option>
                    </select>
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        Specific event
                    </label>
                    <select
                        v-model="specificEventId"
                        :disabled="!eventOptions.length"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                    >
                        <option value="">All events</option>
                        <option
                            v-for="event in eventOptions"
                            :key="event.id"
                            :value="String(event.id)"
                        >
                            {{ event.label }}
                        </option>
                    </select>
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        App availability
                    </label>
                    <select
                        v-model="appEnabledFilter"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                    >
                        <option value="all">All companies</option>
                        <option value="enabled">App enabled</option>
                        <option value="disabled">App disabled</option>
                    </select>
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        Registered from
                    </label>
                    <input
                        v-model="dateFrom"
                        type="date"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                    >
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        Registered to
                    </label>
                    <input
                        v-model="dateTo"
                        type="date"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                    >
                </div>

                <div class="flex flex-col gap-1">
                    <label class="font-semibold uppercase tracking-wide text-xs text-gray-500">
                        Sort by
                    </label>
                    <select
                        v-model="sortOption"
                        class="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-myflowPrimary focus:outline-none focus:ring-1 focus:ring-myflowPrimary"
                    >
                        <option value="registration-desc">Registration · Newest first</option>
                        <option value="registration-asc">Registration · Oldest first</option>
                        <option value="active-first">Status · Active first</option>
                        <option value="inactive-first">Status · Inactive first</option>
                        <option value="note-newest">Last CRM note · Newest first</option>
                        <option value="note-oldest">Last CRM note · Oldest first</option>
                        <option value="note-none">Last CRM note · No note first</option>
                    </select>
                </div>
            </div>
            <div class="flex justify-end gap-3 mt-2">
                <button-secondary
                    v-if="showFilters"
                    size="sm"
                    type="button"
                    class="whitespace-nowrap"
                    @click="resetFilters"
                >
                    Reset filters
                </button-secondary>
                <button-secondary
                    size="sm"
                    type="button"
                    class="whitespace-nowrap"
                    @click="showFilters = ! showFilters"
                >
                    {{ showFilters ? 'Hide filters' : 'Show filters' }}
                </button-secondary>
            </div>
        </div>

        <div class="mt-3 flex flex-wrap items-center justify-between text-xs text-gray-500">
            <span>
                Showing {{ visibleRegistrations.length }} of {{ filteredAndSorted.length }}
                {{ filteredAndSorted.length === 1 ? 'registration' : 'registrations' }}
            </span>
            <span v-if="dateFrom || dateTo">
                Filtered by date
                <template v-if="dateFrom">from {{ formatDateLabel(dateFrom) }}</template>
                <template v-if="dateFrom && dateTo"> to </template>
                <template v-if="dateTo"> {{ formatDateLabel(dateTo) }}</template>
            </span>
        </div>

        <div class="mt-4 space-y-3">
            <div
                v-if="visibleRegistrations.length === 0"
                class="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-600"
            >
                No registrations match the current filters. Try adjusting the filters or search term.
            </div>

            <list-basic
                v-else
                class="-mx-4"
                list-class=""
            >
                <list-basic-item
                    v-for="registration in visibleRegistrations"
                    :key="registration.id"
                >
                    <template #status>
                        <div class="flex flex-row items-center pr-4 md:flex-col md:-ml-3.5">
                            <div
                                class="flex h-4 w-4 items-center justify-center rounded-full"
                                :class="registration.is_active ? 'bg-myflowGreen' : 'bg-myflowYellow'"
                            ></div>
                            <div class="ml-2 text-center text-xxs text-gray-500 md:ml-0 md:mt-1.5 md:max-w-[8rem]">
                                {{ registration.is_active ? 'Active' : 'Not active' }}
                                <template v-if="registration.is_active">
                                    <span class="font-semibold">· {{ registration.plan_label }}</span>
                                </template>
                            </div>
                        </div>
                    </template>

                    <div class="grid-cols-2 gap-8 lg:grid">
                        <div class="col-span-1 mb-2 lg:mb-0">
                            <div class="flex flex-wrap items-center gap-2">
                                <span class="text-sm font-semibold text-gray-900">
                                    {{ registration.company_name }}
                                </span>
                                <span
                                    class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium uppercase tracking-wide text-gray-600"
                                >
                                    {{ registration.plan_label }}
                                </span>
                                <span
                                    v-if="registration.has_paid_subscription"
                                    class="inline-flex items-center rounded-full bg-myflowPrimary/10 px-2 py-1 text-xs font-semibold text-myflowPrimary"
                                >
                                    Paid
                                </span>
                                <span
                                    v-if="registration.app_enabled"
                                    class="inline-flex items-center rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700"
                                >
                                    App enabled
                                </span>
                            </div>
                            <div class="mt-1 text-xs text-gray-500">
                                Registered {{ registration.registered_display ?? '—' }}
                            </div>
                            <div class="mt-1 text-xs text-gray-500">
                                Last CRM note {{
                                    registration.crm_has_lead ? (registration.crm_latest_note_display ?? 'never') : 'n/a'
                                }}
                                <template v-if="registration.crm_has_lead && registration.crm_latest_note_author">
                                    · {{ registration.crm_latest_note_author }}
                                </template>
                            </div>
                            <div class="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                                <span
                                    v-for="feature in moduleFeatures"
                                    :key="feature.key"
                                    class="flex items-center gap-1 cursor-default"
                                    :title="feature.description"
                                >
                                    <span
                                        class="inline-flex h-4 w-4 items-center justify-center rounded border text-[9px] font-semibold"
                                        :class="moduleCheckboxClass(Boolean(registration.modules?.[feature.key]))"
                                    >
                                        ✓
                                    </span>
                                    {{ feature.label }}
                                </span>
                            </div>
                            <div
                                v-if="registration.activity"
                                class="mt-3 w-full"
                            >
                                <div v-if="registration.activity">
                                    <div class="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                                        <div class="flex items-center justify-between gap-3">
                                            <div class="flex-1">
                                                <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                    Activity
                                                </div>
                                                <div
                                                    class="mt-1 text-sm font-semibold"
                                                    :class="activityComparisonClass(activityPosition(registration))"
                                                >
                                                    {{ activityComparisonLabel(activityPosition(registration)) }}
                                                </div>
                                                <div class="mt-1 text-xxs text-gray-500">
                                                    {{ activityContextText(registration) }}
                                                </div>
                                            </div>
                                            <div :class="activityIndicatorClass(activityPosition(registration))">
                                            <span class="text-xl font-bold">
                                                {{ activityIndicatorIcon(activityPosition(registration)) }}
                                            </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-else
                                    class="text-xs text-gray-500"
                                >
                                    Activity data not available yet.
                                </div>
                            </div>
                        </div>
                        <div class="col-span-1 flex flex-col items-end gap-2 text-sm text-gray-800">
                            <div class="text-right">
                                <div>{{ registration.primary_contact_name ?? '—' }}</div>
                                <div class="text-xs text-gray-500">{{ registration.primary_contact_email ?? '—' }}</div>
                            </div>
                            <div class="flex flex-wrap items-center justify-end gap-2 text-xs">
                                <template v-if="registration.crm_allowed">
                                    <a
                                        v-if="registration.crm_has_lead && registration.crm_lead_url"
                                        :href="registration.crm_lead_url"
                                        target="_blank"
                                        rel="noopener"
                                        :class="crmActionButtonClass"
                                    >
                                        Open in CRM
                                    </a>
                                    <a
                                        v-else-if="registration.crm_create_url"
                                        :href="registration.crm_create_url"
                                        target="_blank"
                                        rel="noopener"
                                        :class="crmActionButtonClass"
                                    >
                                        Add to CRM
                                    </a>
                                </template>
                                <a
                                    v-if="hasEvents(registration)"
                                    :class="crmActionButtonClass"
                                    role="button"
                                    @click.prevent="openEvents(registration)"
                                >
                                    Webinars & events
                                </a>
                            </div>
                        </div>
                    </div>

                    <!-- Commissions Fold-out -->
                    <div v-if="registration.has_commissions" class="mt-3 w-full">
                        <div class="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                            <button
                                @click="toggleCommissions(registration.id)"
                                class="flex w-full items-center justify-between text-left"
                            >
                                <div class="flex-1">
                                    <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        Commissions
                                    </div>
                                    <div class="mt-1 flex items-center gap-2">
                                        <div class="text-sm font-semibold text-gray-900">
                                            {{ formatCurrency(registration.total_commissions) }} {{ registration.commissions[0]?.currency?.toUpperCase() || 'EUR' }}
                                        </div>
                                        <span class="text-xs text-gray-500">
                                            ({{ registration.commissions.length }} {{ registration.commissions.length === 1 ? 'commission' : 'commissions' }})
                                        </span>
                                    </div>
                                    <div class="mt-1 text-xxs text-gray-500">
                                        <span class="inline-flex items-center gap-1">
                                            <span
                                                class="inline-flex h-2 w-2 rounded-full"
                                                :class="{
                                                    'bg-green-500': registration.commissions.some(c => c.status === 'paid'),
                                                    'bg-yellow-500': registration.commissions.every(c => c.status === 'mapped') && !registration.commissions.some(c => c.status === 'paid'),
                                                    'bg-gray-400': registration.commissions.every(c => c.status === 'unmapped')
                                                }"
                                            ></span>
                                            {{ getCommissionStatusSummary(registration.commissions) }}
                                        </span>
                                    </div>
                                </div>
                                <div class="ml-4 text-gray-400">
                                    <i
                                        class="fas text-sm transition-transform"
                                        :class="expandedCommissions.has(registration.id) ? 'fa-chevron-up' : 'fa-chevron-down'"
                                    ></i>
                                </div>
                            </button>

                            <div
                                v-if="expandedCommissions.has(registration.id)"
                                class="mt-4 space-y-3 border-t border-gray-100 pt-3"
                            >
                                <div
                                    v-for="commission in registration.commissions"
                                    :key="commission.id"
                                    class="rounded-lg border border-gray-200 bg-gray-50 p-3"
                                >
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="flex-1">
                                            <div class="flex flex-wrap items-center gap-2">
                                                <span
                                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide"
                                                    :class="commission.type === 'premier_partner'
                                                        ? 'bg-purple-100 text-purple-800'
                                                        : 'bg-blue-100 text-blue-800'"
                                                >
                                                    {{ commission.type_label }}
                                                </span>
                                                <span
                                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                                    :class="{
                                                        'bg-green-100 text-green-800': commission.status === 'paid',
                                                        'bg-yellow-100 text-yellow-800': commission.status === 'mapped',
                                                        'bg-gray-100 text-gray-800': commission.status === 'unmapped'
                                                    }"
                                                >
                                                    {{ commission.status === 'paid' ? 'Paid' : commission.status === 'mapped' ? 'Pending' : 'Unmapped' }}
                                                </span>
                                            </div>
                                            <div class="mt-2 text-sm font-semibold text-gray-900">
                                                {{ formatCurrency(commission.amount) }} {{ commission.currency?.toUpperCase() || 'EUR' }}
                                            </div>
                                            <div class="mt-1.5 space-y-1 text-xs text-gray-600">
                                                <div v-if="commission.transaction_date_display">
                                                    <span class="text-gray-500">Date:</span> {{ commission.transaction_date_display }}
                                                </div>
                                                <div v-if="commission.stripe_id" class="font-mono text-gray-400">
                                                    {{ commission.stripe_id }}
                                                </div>
                                                <div v-if="commission.product_description" class="text-gray-600">
                                                    {{ commission.product_description }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </list-basic-item>
            </list-basic>
        </div>

        <div
            v-if="showControls"
            class="mt-4 flex gap-3"
        >
            <button-base
                v-if="canShowMore"
                size="sm"
                type="button"
                class="whitespace-nowrap"
                @click="showMore"
            >
                Show more registrations
            </button-base>
            <button-secondary
                v-if="canShowLess"
                size="sm"
                type="button"
                class="whitespace-nowrap"
                @click="showLess"
            >
                Collapse list
            </button-secondary>
        </div>

        <slideout-with-slots
            v-if="eventsSlideoutOpen"
            @close="closeEvents"
        >
            <template #title>
                {{ selectedRegistration?.company_name ?? 'Webinars & events' }}
            </template>
            <template #subtitle>
                <span v-if="selectedRegistration?.primary_contact_name">
                    Primary contact: {{ selectedRegistration.primary_contact_name }}
                </span>
            </template>
            <template #body>
                <div
                    v-if="!selectedRegistration || !hasEvents(selectedRegistration)"
                    class="text-sm text-gray-500"
                >
                    No webinar activity yet.
                </div>
                <div
                    v-else
                    class="space-y-8 text-sm text-gray-800"
                >
                    <section v-if="upcomingEvents.length">
                        <h4 class="text-sm font-semibold text-gray-900">Upcoming events</h4>
                        <ul class="mt-3 space-y-3">
                            <li
                                v-for="event in upcomingEvents"
                                :key="`upcoming-${event.registration_id}-${event.webinar_id}`"
                            >
                                <div class="rounded-lg border border-gray-200 p-3">
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="font-medium text-gray-900">{{ event.title }}</div>
                                        <span class="text-xs font-semibold uppercase text-myflowPrimary">Upcoming</span>
                                    </div>
                                    <div class="mt-2 text-xs text-gray-600">
                                        Starts {{ event.start_display ?? 'TBA' }}
                                    </div>
                                    <div
                                        v-if="participantDisplay(event)"
                                        class="mt-1 text-xs text-gray-500"
                                    >
                                        {{ participantDisplay(event) }}
                                    </div>
                                    <div class="mt-2">
                                        <a
                                            v-if="event.page_url"
                                            :href="event.page_url"
                                            target="_blank"
                                            rel="noopener"
                                            class="text-xs mf-link"
                                        >
                                            View event page
                                        </a>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </section>

                    <section v-if="pastEvents.length">
                        <h4 class="text-sm font-semibold text-gray-900">Past events</h4>
                        <ul class="mt-3 space-y-3">
                            <li
                                v-for="event in pastEvents"
                                :key="`past-${event.registration_id}-${event.webinar_id}`"
                            >
                                <div class="rounded-lg border border-gray-200 p-3">
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="font-medium text-gray-900">{{ event.title }}</div>
                                        <span
                                            class="text-xs font-semibold uppercase"
                                            :class="event.has_joined ? 'text-myflowPrimary' : 'text-gray-400'"
                                        >
                                            {{ event.has_joined ? 'Attended' : 'Registered' }}
                                        </span>
                                    </div>
                                    <div class="mt-2 text-xs text-gray-600">
                                        Took place {{ event.start_display ?? 'Earlier' }}
                                    </div>
                                    <div
                                        v-if="event.has_joined && event.last_joined_display"
                                        class="mt-1 text-xs text-gray-500"
                                    >
                                        Last joined {{ event.last_joined_display }}
                                    </div>
                                    <div
                                        v-if="participantDisplay(event)"
                                        class="mt-1 text-xs text-gray-500"
                                    >
                                        {{ participantDisplay(event) }}
                                    </div>
                                    <div class="mt-2">
                                        <a
                                            v-if="event.page_url"
                                            :href="event.page_url"
                                            target="_blank"
                                            rel="noopener"
                                            class="text-xs mf-link"
                                        >
                                            View event page
                                        </a>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </section>

                    <div
                        v-if="!upcomingEvents.length && !pastEvents.length"
                        class="text-xs text-gray-500"
                    >
                        No webinar activity recorded yet.
                    </div>
                </div>
            </template>
        </slideout-with-slots>
    </div>
</template>

<script setup>
import {computed, ref, watch} from 'vue';

const INITIAL_VISIBLE = 10;
const VISIBLE_INCREMENT = 10;

const moduleFeatures = [
    {
        key: 'landing_page',
        label: 'Landing page',
        description: 'Customer has published at least one landing or website page.',
    },
    {
        key: 'expert_contract',
        label: 'Expert contract',
        description: 'Customer has sent at least one expert contract.',
    },
    {
        key: 'lead_hunter',
        label: 'Lead Hunter',
        description: 'Customer has created at least one Lead Hunter campaign.',
    },
];

const ACTIVITY_COMPARISON_LABELS = {
    above: 'Above peers',
    below: 'Below peers',
    on_par: 'In line with peers',
    unavailable: 'Benchmark unavailable',
};

const activityComparisonLabel = (position) => {
    if (!position) {
        return ACTIVITY_COMPARISON_LABELS.unavailable;
    }

    return ACTIVITY_COMPARISON_LABELS[position] ?? ACTIVITY_COMPARISON_LABELS.unavailable;
};

const activityComparisonClass = (position) => {
    switch (position) {
        case 'above':
            return 'text-myflowGreen';
        case 'below':
            return 'text-red-500';
        case 'on_par':
            return 'text-gray-700';
        default:
            return 'text-gray-400';
    }
};

const activityIndicatorIcon = (position) => {
    switch (position) {
        case 'above':
            return '▲';
        case 'below':
            return '▼';
        case 'on_par':
            return '—';
        default:
            return '—';
    }
};

const activityIndicatorClass = (position) => {
    switch (position) {
        case 'above':
            return 'flex h-10 w-10 items-center justify-center rounded-full border border-myflowGreen bg-myflowGreen/10 text-myflowGreen';
        case 'below':
            return 'flex h-10 w-10 items-center justify-center rounded-full border border-red-400 bg-red-50 text-red-500';
        case 'on_par':
            return 'flex h-10 w-10 items-center justify-center rounded-full border border-gray-400 bg-gray-100 text-gray-700';
        default:
            return 'flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400';
    }
};

const activityPosition = (registration) => registration?.activity?.position ?? null;

const activityContextText = (registration) => {
    const cohortLabel = registration?.activity?.cohort?.label;

    if (!cohortLabel) {
        return 'Peer benchmark not available yet.';
    }

    const base = `Compared with peers who ${cohortLabel}.`;

    if (registration?.activity?.available === false) {
        return `${base} Benchmark will appear once more peers have activity.`;
    }

    return base;
};

const props = defineProps({
    registrations: {
        type: Array,
        default: () => [],
    },
    eventOptions: {
        type: Array,
        default: () => [],
    },
});

const searchTerm = ref('');
const subscriptionFilter = ref('all');
const statusFilter = ref('all');
const sortOption = ref('registration-desc');
const dateFrom = ref('');
const dateTo = ref('');
const visibleCount = ref(INITIAL_VISIBLE);
const eventsSlideoutOpen = ref(false);
const selectedRegistration = ref(null);
const activityFilter = ref('any');
const eventAttendanceFilter = ref('all');
const specificEventId = ref('');
const appEnabledFilter = ref('all');
const showFilters = ref(false);
const expandedCommissions = ref(new Set());

const moduleCheckboxClass = (active) => (active ? 'bg-myflowGreen border-myflowGreen text-white' : 'bg-white border-gray-300 text-transparent');

const crmActionButtonClass = 'inline-flex items-center rounded-full border border-gray-300 px-3 py-1 font-medium text-gray-700 hover:border-myflowPrimary hover:text-myflowPrimary';

const normalise = (value) => (value || '').toString().trim().toLowerCase();

const parseDateInput = (value, endOfDay = false) => {
    if (!value) {
        return null;
    }

    const suffix = endOfDay ? 'T23:59:59' : 'T00:00:00';
    const date = new Date(`${value}${suffix}`);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
};

const getRegisteredDate = (registration) => {
    if (!registration?.registered_at_iso) {
        return null;
    }

    const date = new Date(registration.registered_at_iso);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
};

const getNoteDate = (registration) => {
    if (!registration?.crm_latest_note_iso) {
        return null;
    }

    const date = new Date(registration.crm_latest_note_iso);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date;
};

const matchesSubscriptionFilter = (registration) => {
    const plan = registration.plan_key || 'basic';
    const isActive = Boolean(registration.is_active);
    const isPaid = Boolean(registration.has_paid_subscription);

    switch (subscriptionFilter.value) {
        case 'pro':
            return plan === 'pro';
        case 'essentials':
            return plan === 'essentials';
        case 'paid':
            return isPaid;
        case 'none':
            return !isPaid;
        case 'all':
        default:
            return true;
    }
};

const matchesStatusFilter = (registration) => {
    if (statusFilter.value === 'active') {
        return Boolean(registration.is_active);
    }

    if (statusFilter.value === 'inactive') {
        return !registration.is_active;
    }

    return true;
};

const matchesSearch = (registration) => {
    const term = normalise(searchTerm.value);

    if (!term) {
        return true;
    }

    const company = normalise(registration.company_name);
    const contact = normalise(registration.primary_contact_name);
    const email = normalise(registration.primary_contact_email);
    const noteAuthor = normalise(registration.crm_latest_note_author);

    return (
        (company && company.includes(term)) ||
        (contact && contact.includes(term)) ||
        (email && email.includes(term)) ||
        (noteAuthor && noteAuthor.includes(term))
    );
};

const matchesDateRange = (registration) => {
    const registeredDate = getRegisteredDate(registration);

    if (!registeredDate) {
        return true;
    }

    const fromDate = parseDateInput(dateFrom.value, false);
    const toDate = parseDateInput(dateTo.value, true);

    if (fromDate && registeredDate < fromDate) {
        return false;
    }

    if (toDate && registeredDate > toDate) {
        return false;
    }

    return true;
};

const registrationHasEventAttendance = (registration) => Boolean(registration?.events?.has_attended);

const collectRegistrationEvents = (registration) => {
    if (!registration) {
        return [];
    }

    const aggregated = [];
    const company = registration.events ?? {};
    const contact = registration.contact_events ?? {};

    (company.past ?? []).forEach((event) => aggregated.push(event));
    (company.upcoming ?? []).forEach((event) => aggregated.push(event));
    (contact.past ?? []).forEach((event) => aggregated.push(event));
    (contact.upcoming ?? []).forEach((event) => aggregated.push(event));

    return aggregated;
};

const matchesEventAttendanceFilter = (registration) => {
    switch (eventAttendanceFilter.value) {
        case 'attended':
            return registrationHasEventAttendance(registration);
        case 'not_attended':
            return !registrationHasEventAttendance(registration);
        case 'all':
        default:
            return true;
    }
};

const matchesSpecificEventFilter = (registration) => {
    if (!specificEventId.value) {
        return true;
    }

    const targetId = String(specificEventId.value);
    const events = collectRegistrationEvents(registration);

    return events.some((event) => {
        if (!event) {
            return false;
        }

        const eventId = event.webinar_id ?? event.id ?? null;

        if (eventId === null || String(eventId) !== targetId) {
            return false;
        }

        return Boolean(event.has_joined);
    });
};

const matchesAppEnabledFilter = (registration) => {
    switch (appEnabledFilter.value) {
        case 'enabled':
            return Boolean(registration?.app_enabled);
        case 'disabled':
            return !registration?.app_enabled;
        case 'all':
        default:
            return true;
    }
};

const matchesActivityFilters = (registration) => {
    const filterValue = activityFilter.value ?? 'any';

    if (!filterValue || filterValue === 'any') {
        return true;
    }

    const available = registration?.activity?.available ?? false;
    const position = registration?.activity?.position ?? null;

    if (filterValue === 'unavailable') {
        return !available;
    }

    if (!available || !position) {
        return false;
    }

    return position === filterValue;
};

const filteredRegistrations = computed(() => {
    return props.registrations.filter((registration) => {
        return (
            matchesSearch(registration) &&
            matchesSubscriptionFilter(registration) &&
            matchesStatusFilter(registration) &&
            matchesDateRange(registration) &&
            matchesEventAttendanceFilter(registration) &&
            matchesSpecificEventFilter(registration) &&
            matchesAppEnabledFilter(registration) &&
            matchesActivityFilters(registration)
        );
    });
});

const filteredAndSorted = computed(() => {
    const registrations = filteredRegistrations.value.slice();

    registrations.sort((a, b) => {
        switch (sortOption.value) {
            case 'registration-asc': {
                const dateA = getRegisteredDate(a);
                const dateB = getRegisteredDate(b);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateA - dateB;
            }
            case 'active-first': {
                if (a.is_active === b.is_active) {
                    const dateA = getRegisteredDate(a);
                    const dateB = getRegisteredDate(b);
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    return dateB - dateA;
                }
                return a.is_active ? -1 : 1;
            }
            case 'inactive-first': {
                if (a.is_active === b.is_active) {
                    const dateA = getRegisteredDate(a);
                    const dateB = getRegisteredDate(b);
                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;
                    return dateB - dateA;
                }
                return a.is_active ? 1 : -1;
            }
            case 'note-newest': {
                const noteA = getNoteDate(a);
                const noteB = getNoteDate(b);
                if (!noteA && !noteB) return 0;
                if (!noteA) return 1;
                if (!noteB) return -1;
                return noteB - noteA;
            }
            case 'note-oldest': {
                const noteA = getNoteDate(a);
                const noteB = getNoteDate(b);
                if (!noteA && !noteB) return 0;
                if (!noteA) return 1;
                if (!noteB) return -1;
                return noteA - noteB;
            }
            case 'note-none': {
                const noteA = getNoteDate(a);
                const noteB = getNoteDate(b);

                if (!noteA && !noteB) {
                    const regA = getRegisteredDate(a);
                    const regB = getRegisteredDate(b);
                    if (!regA && !regB) return 0;
                    if (!regA) return 1;
                    if (!regB) return -1;
                    return regA - regB;
                }

                if (!noteA) {
                    return -1;
                }

                if (!noteB) {
                    return 1;
                }

                return noteA - noteB;
            }
            case 'registration-desc':
            default: {
                const dateA = getRegisteredDate(a);
                const dateB = getRegisteredDate(b);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateB - dateA;
            }
        }
    });

    return registrations;
});

const visibleRegistrations = computed(() => {
    if (filteredAndSorted.value.length <= INITIAL_VISIBLE) {
        return filteredAndSorted.value;
    }

    return filteredAndSorted.value.slice(0, visibleCount.value);
});

const canShowMore = computed(() => filteredAndSorted.value.length > visibleCount.value);
const canShowLess = computed(
    () =>
        filteredAndSorted.value.length > INITIAL_VISIBLE &&
        visibleCount.value > INITIAL_VISIBLE,
);

const showControls = computed(
    () => filteredAndSorted.value.length > INITIAL_VISIBLE || canShowLess.value,
);

const showMore = () => {
    visibleCount.value = Math.min(
        filteredAndSorted.value.length,
        visibleCount.value + VISIBLE_INCREMENT,
    );
};

const showLess = () => {
    visibleCount.value = INITIAL_VISIBLE;
};

const resolveEventsSource = (registration) => {
    if (!registration) {
        return null;
    }

    const contact = registration.contact_events ?? null;

    const hasContactEvents = contact && ((Array.isArray(contact.past) && contact.past.length > 0) || (Array.isArray(contact.upcoming) && contact.upcoming.length > 0));

    if (hasContactEvents) {
        return contact;
    }

    return registration.events ?? null;
};

const hasEvents = (registration) => {
    const events = resolveEventsSource(registration);

    if (!events) {
        return false;
    }

    const pastCount = Array.isArray(events.past) ? events.past.length : 0;
    const upcomingCount = Array.isArray(events.upcoming) ? events.upcoming.length : 0;

    return pastCount + upcomingCount > 0;
};

const openEvents = (registration) => {
    if (!hasEvents(registration)) {
        return;
    }

    selectedRegistration.value = registration;
    eventsSlideoutOpen.value = true;
};

const closeEvents = () => {
    eventsSlideoutOpen.value = false;
    selectedRegistration.value = null;
};

const upcomingEvents = computed(() => {
    const events = resolveEventsSource(selectedRegistration.value);

    if (!events) {
        return [];
    }

    return events.upcoming ?? [];
});

const pastEvents = computed(() => {
    const events = resolveEventsSource(selectedRegistration.value);

    if (!events) {
        return [];
    }

    return events.past ?? [];
});

const participantDisplay = (event) => {
    if (!event?.participant_name) {
        return '';
    }

    if (event.participant_email) {
        return `${event.participant_name} (${event.participant_email})`;
    }

    return event.participant_name;
};

const toggleCommissions = (registrationId) => {
    if (expandedCommissions.value.has(registrationId)) {
        expandedCommissions.value.delete(registrationId);
    } else {
        expandedCommissions.value.add(registrationId);
    }
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

const getCommissionStatusSummary = (commissions) => {
    const paid = commissions.filter(c => c.status === 'paid').length;
    const pending = commissions.filter(c => c.status === 'mapped').length;
    const unmapped = commissions.filter(c => c.status === 'unmapped').length;

    const parts = [];
    if (paid > 0) parts.push(`${paid} paid`);
    if (pending > 0) parts.push(`${pending} pending`);
    if (unmapped > 0) parts.push(`${unmapped} unmapped`);

    return parts.length > 0 ? parts.join(', ') : 'No commissions';
};

const resetFilters = () => {
    searchTerm.value = '';
    subscriptionFilter.value = 'all';
    statusFilter.value = 'all';
    sortOption.value = 'registration-desc';
    dateFrom.value = '';
    dateTo.value = '';
    activityFilter.value = 'any';
    eventAttendanceFilter.value = 'all';
    specificEventId.value = '';
    appEnabledFilter.value = 'all';
};

watch(
    [searchTerm, subscriptionFilter, statusFilter, sortOption, dateFrom, dateTo],
    () => {
        visibleCount.value = INITIAL_VISIBLE;
    },
);

watch(activityFilter, () => {
    visibleCount.value = INITIAL_VISIBLE;
});

watch(eventAttendanceFilter, (value) => {
    if (value === 'not_attended' && specificEventId.value) {
        specificEventId.value = '';
    }

    visibleCount.value = INITIAL_VISIBLE;
});

watch(specificEventId, (value) => {
    if (value && eventAttendanceFilter.value === 'not_attended') {
        eventAttendanceFilter.value = 'attended';
    }

    visibleCount.value = INITIAL_VISIBLE;
});

watch(appEnabledFilter, () => {
    visibleCount.value = INITIAL_VISIBLE;
});

watch(
    () => props.eventOptions,
    (options) => {
        if (!options?.length) {
            if (specificEventId.value) {
                specificEventId.value = '';
            }

            return;
        }

        if (
            specificEventId.value &&
            !options.some((option) => String(option.id) === String(specificEventId.value))
        ) {
            specificEventId.value = '';
        }
    },
    {deep: true},
);

const formatDateLabel = (value) => {
    if (!value) {
        return '';
    }

    try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch (error) {
        return value;
    }
};
</script>
